import { ThemeEvents } from "@theme/events";
import { formatCurrency } from "@theme/utilities";

/**
 * Packung UI from variant metafield custom.pdp_packung × quantity selector → cart qty.
 *
 * Markup:
 * - product-form-component[data-ve-enabled]
 * - [data-ve-pack-count] / [data-ve-cart-quantity]
 * - [data-we-site-packung] with [data-we-site-packung-options]
 * - script[data-ve-packung-by-variant] JSON map of variantId → entries
 */

const CHECK_SVG = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false"><path d="M4.5 8.2 6.8 10.5 11.5 5.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/**
 * @typedef {{ veSize: number, label: string, discountPct: number }} PackungEntry
 */

class WeSiteVeQuantity {
  /** @param {HTMLElement} formComponent */
  constructor(formComponent) {
    this.root = formComponent;
    this.abort = new AbortController();
    /** @type {number} */
    this.#unitPriceCents = 0;
    /** @type {number} One-time variant price (fallback when no selling plan) */
    this.#baseUnitPriceCents = 0;
    /** @type {number} Variant compare_at (UVP), 0 when unset */
    this.#compareAtCents = 0;
    /** @type {number | null} */
    this.#unitPricePerRef = null;
    /** @type {string} */
    this.#referenceUnit = "kg";
    /** @type {Record<string, PackungEntry[]>} */
    this.#packungByVariant = {};
    this.#parsePackungByVariant();
    this.#seedUnitPriceFromDom();
    this.#seedCompareAtFromDom();
    this.#bind();
    this.#applyPackungForVariant(this.#variantId());
    this.sync();
  }

  /** @type {number} */
  #unitPriceCents;
  /** @type {number} */
  #baseUnitPriceCents;
  /** @type {number} */
  #compareAtCents;
  /** @type {number | null} */
  #unitPricePerRef;
  /** @type {string} */
  #referenceUnit;
  /** @type {Record<string, PackungEntry[]>} */
  #packungByVariant;

  disconnect() {
    this.abort.abort();
  }

  #seedUnitPriceFromDom() {
    if (this.#unitPriceCents > 0) return;
    const root = this.#packungRoots()[0];
    const fromRoot = Number(root?.dataset.variantPriceCents);
    if (Number.isFinite(fromRoot) && fromRoot > 0) {
      this.#unitPriceCents = fromRoot;
      this.#baseUnitPriceCents = fromRoot;
      return;
    }
    const radio = this.#selectedPackung();
    if (!radio) return;
    const pack = Number(radio.dataset.packPriceCents);
    const ve = Number(radio.dataset.veSize) || 1;
    const disc = Number(radio.dataset.discountPct) || 0;
    if (!(pack >= 0) || ve < 1) return;
    const undisc = disc > 0 && disc < 100 ? pack / ((100 - disc) / 100) : pack;
    this.#unitPriceCents = Math.round(undisc / ve);
    this.#baseUnitPriceCents = this.#unitPriceCents;
  }

  #seedCompareAtFromDom() {
    if (this.#compareAtCents > 0) return;
    const root = this.#packungRoots()[0];
    const fromRoot = Number(root?.dataset.variantCompareAtCents);
    if (Number.isFinite(fromRoot) && fromRoot > 0) {
      this.#compareAtCents = fromRoot;
      return;
    }
    // Fallback: derive UVP unit from a tile that already has a compare total
    const radio = this.#selectedPackung() || this.#scope().querySelector("[data-we-site-packung] input[type='radio']");
    if (!(radio instanceof HTMLInputElement)) return;
    const comparePack = Number(radio.dataset.packCompareCents);
    const ve = Number(radio.dataset.veSize) || 1;
    if (comparePack > 0 && ve > 0) this.#compareAtCents = Math.round(comparePack / ve);
  }

  /**
   * Pack strikethrough cents: UVP × VE, or pre-multipack total when only Packung-% applies.
   * @param {number} veSize
   * @param {number} discountPct
   * @param {number} packCents
   * @param {number} unitCents
   */
  #packCompareCents(veSize, discountPct, packCents, unitCents) {
    const ve = veSize > 0 ? veSize : 1;
    let compare = 0;
    const base = this.#baseUnitPriceCents;
    if (this.#compareAtCents > 0 && (base <= 0 || this.#compareAtCents > base)) {
      compare = this.#compareAtCents * ve;
    } else if (discountPct > 0) {
      compare = unitCents * ve;
    }
    return compare > packCents ? compare : 0;
  }

  #scope() {
    return (
      this.root.closest(".product-information, .shopify-section, dialog, .quick-add__content") || document
    );
  }

  #moneyFormat() {
    const atc = this.root.querySelector("add-to-cart-component");
    return atc instanceof HTMLElement ? atc.dataset.moneyFormat : undefined;
  }

  #parsePackungByVariant() {
    const script =
      this.#scope().querySelector(
        `variant-picker[data-product-id="${this.root.dataset.productId}"] script[data-ve-packung-by-variant]`
      ) || this.#scope().querySelector("script[data-ve-packung-by-variant]");
    if (!(script instanceof HTMLScriptElement)) return;
    try {
      const parsed = JSON.parse(script.textContent || "{}");
      if (parsed && typeof parsed === "object") this.#packungByVariant = parsed;
    } catch {
      this.#packungByVariant = {};
    }
  }

  #bind() {
    const { signal } = this.abort;
    const packControl = this.#packControl();
    packControl?.addEventListener("change", () => this.sync(), { signal });
    packControl?.addEventListener("input", () => this.sync(), { signal });

    const scope = this.#scope();
    scope.addEventListener(
      "change",
      (event) => {
        const target = /** @type {EventTarget | null} */ (event.target);
        if (!(target instanceof HTMLElement)) return;
        if (!target.closest("[data-we-site-packung]")) return;
        this.sync();
      },
      { signal }
    );
    scope.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    document.addEventListener("selling-plan:changed", this.#onSellingPlanChanged, { signal });
  }

  /** @type {EventListener} */
  #onSellingPlanChanged = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    const planId = detail.sellingPlanId;
    const planPrice = Number(detail.sellingPlanPrice);

    if (planId && Number.isFinite(planPrice) && planPrice > 0) {
      this.#unitPriceCents = planPrice;
    } else {
      this.#unitPriceCents = this.#baseUnitPriceCents || this.#unitPriceCents;
    }

    this.#applyPackungForVariant(this.#variantId());
    this.sync();
  };

  /** @type {EventListener} */
  #onVariantUpdate = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    const productId = this.root.dataset.productId;
    if (detail?.data?.productId && productId && String(detail.data.productId) !== String(productId)) {
      return;
    }

    const resource = detail?.resource;
    if (resource?.price != null) {
      this.#baseUnitPriceCents = Number(resource.price) || 0;
      // Keep selling-plan unit if abo is selected; otherwise use one-time price
      const sellingPlanInput = /** @type {HTMLInputElement | null} */ (
        this.root.querySelector('input.selected-selling-plan-id, input[name="selling_plan"]')
      );
      const planSelect = this.#scope().querySelector("[data-we-site-abonnement-plan]");
      const hasActivePlan = Boolean(sellingPlanInput?.value);
      if (hasActivePlan && planSelect instanceof HTMLSelectElement) {
        const planPrice = Number(planSelect.selectedOptions?.[0]?.dataset?.price);
        this.#unitPriceCents =
          Number.isFinite(planPrice) && planPrice > 0 ? planPrice : this.#baseUnitPriceCents;
      } else {
        this.#unitPriceCents = this.#baseUnitPriceCents;
      }
    }
    if (resource && "compare_at_price" in resource) {
      this.#compareAtCents = Number(resource.compare_at_price) || 0;
    }
    if (resource?.unit_price != null) this.#unitPricePerRef = Number(resource.unit_price);
    const refUnit = resource?.unit_price_measurement?.reference_unit;
    if (refUnit) this.#referenceUnit = String(refUnit);

    const variantId = resource?.id != null ? String(resource.id) : this.#variantId();

    queueMicrotask(() => {
      // Morph may replace packung + JSON — re-read map, then apply variant entries
      this.#parsePackungByVariant();
      this.#applyPackungForVariant(variantId);
      this.sync();
    });
  };

  #variantId() {
    const input =
      this.root.querySelector('input[name="id"]') || this.root.querySelector('[ref="variantId"]');
    if (!(input instanceof HTMLInputElement)) return null;
    return input.value || null;
  }

  /**
   * Rebuild Packung tiles for the selected size variant (variant metafield).
   * @param {string | null} variantId
   */
  #applyPackungForVariant(variantId) {
    const roots = this.#packungRoots();
    if (!roots.length) {
      this.#renumberBuyBoxSteps();
      return;
    }

    const entries = variantId ? this.#packungByVariant[String(variantId)] : null;
    const hasEntries = Array.isArray(entries) && entries.length > 0;
    const preferredVe = this.root.dataset.veSize;

    roots.forEach((root) => {
      if (variantId) root.dataset.variantId = String(variantId);
      root.dataset.variantPriceCents = String(this.#baseUnitPriceCents || 0);
      root.dataset.variantCompareAtCents = String(this.#compareAtCents || 0);
      root.hidden = !hasEntries;
      root.dataset.packungImagesCount = hasEntries ? String(entries.length) : "0";
      root.toggleAttribute("aria-hidden", !hasEntries);

      const optionsHost = root.querySelector("[data-we-site-packung-options]");
      if (!(optionsHost instanceof HTMLElement)) return;

      if (!hasEntries) {
        optionsHost.replaceChildren();
        return;
      }

      const radioName =
        root.querySelector('input[type="radio"][name]')?.getAttribute("name") ||
        `we-site-packung-${this.root.dataset.productId || "p"}`;

      const unitCents = this.#unitPriceCents;
      const moneyFormat = this.#moneyFormat();
      const frag = document.createDocumentFragment();

      let selectedIndex = 0;
      if (preferredVe) {
        const match = entries.findIndex((e) => String(e.veSize) === String(preferredVe));
        if (match >= 0) selectedIndex = match;
      }

      entries.forEach((entry, index) => {
        const veSize = Number(entry.veSize) > 0 ? Number(entry.veSize) : 1;
        const discountPct = Math.min(100, Math.max(0, Number(entry.discountPct) || 0));
        const label = entry.label || "";
        // Per-unit discount (floor), then × veSize — same as cart/ATC
        const discountedUnit =
          discountPct > 0 ? unitCents - Math.floor((unitCents * discountPct) / 100) : unitCents;
        const packCents = discountedUnit * veSize;
        const packCompareCents = this.#packCompareCents(veSize, discountPct, packCents, unitCents);

        const labelEl = document.createElement("label");
        labelEl.className =
          "variant-option__button-label variant-option__button-label--packung focus-within--absolute";

        const input = document.createElement("input");
        input.type = "radio";
        input.name = radioName;
        input.value = String(veSize);
        input.setAttribute("aria-label", label);
        input.dataset.veSize = String(veSize);
        input.dataset.discountPct = String(discountPct);
        input.dataset.packungLabel = label;
        input.dataset.packPriceCents = String(packCents);
        input.dataset.packCompareCents = String(packCompareCents);
        if (index === selectedIndex) input.checked = true;

        const packung = document.createElement("span");
        packung.className = "variant-option__packung";

        const titleRow = document.createElement("span");
        titleRow.className = "variant-option__packung-title-row";
        const text = document.createElement("span");
        text.className = "variant-option__button-label__text";
        const parenIdx = label.indexOf("(");
        if (parenIdx > 0) {
          const nameEl = document.createElement("span");
          nameEl.className = "variant-option__packung-name";
          nameEl.textContent = label.slice(0, parenIdx).trimEnd();
          const veEl = document.createElement("span");
          veEl.className = "variant-option__packung-ve";
          veEl.textContent = label.slice(parenIdx).trim();
          text.append(nameEl, veEl);
        } else {
          const nameEl = document.createElement("span");
          nameEl.className = "variant-option__packung-name";
          nameEl.textContent = label;
          text.appendChild(nameEl);
        }
        titleRow.appendChild(text);
        if (discountPct > 0) {
          const badge = document.createElement("span");
          badge.className = "variant-option__packung-badge";
          badge.textContent = `-${discountPct}%`;
          titleRow.appendChild(badge);
        }

        const prices = document.createElement("span");
        prices.className = "variant-option__packung-prices";
        const priceEl = document.createElement("span");
        priceEl.className = "variant-option__packung-price";
        priceEl.dataset.packungPrice = "";
        priceEl.textContent = formatCurrency(packCents, moneyFormat);
        prices.appendChild(priceEl);

        if (packCompareCents > 0) {
          const compareEl = document.createElement("span");
          compareEl.className = "compare-at-price variant-option__packung-compare";
          compareEl.dataset.packungCompare = "";
          compareEl.textContent = formatCurrency(packCompareCents, moneyFormat);
          prices.appendChild(compareEl);
        }

        packung.append(titleRow, prices);

        const check = document.createElement("span");
        check.className = "variant-option__button-label__check";
        check.setAttribute("aria-hidden", "true");
        check.innerHTML = CHECK_SVG;

        labelEl.append(input, packung, check);
        frag.appendChild(labelEl);
      });

      optionsHost.replaceChildren(frag);
    });

    this.#renumberBuyBoxSteps();
  }

  /**
   * Visible buy-box steps (Menge / Packung / Lieferung / Frequenz) must stay sequential
   * when Packung is hidden for the selected variant.
   */
  #renumberBuyBoxSteps() {
    const scope = this.#scope();
    if (!(scope instanceof Element)) return;

    const stepEls = scope.querySelectorAll(
      ".variant-option__step, .we-site-pdp-abonnement__section-step, .we-site-pdp-abonnement__frequency-step"
    );

    let step = 0;
    stepEls.forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.closest("[hidden], [data-we-site-packung][data-packung-images-count='0']")) return;
      step += 1;
      el.textContent = String(step);
    });
  }

  #packungRoots() {
    const productId = this.root.dataset.productId;
    if (!productId) {
      return [...this.#scope().querySelectorAll("[data-we-site-packung]")];
    }
    return [
      ...this.#scope().querySelectorAll(`[data-we-site-packung][data-product-id="${productId}"]`),
    ];
  }

  #packControl() {
    return /** @type {HTMLInputElement | HTMLSelectElement | null} */ (
      this.root.querySelector("[data-ve-pack-count]")
    );
  }

  #cartQuantityInput() {
    return /** @type {HTMLInputElement | null} */ (this.root.querySelector("[data-ve-cart-quantity]"));
  }

  #selectedPackung() {
    for (const root of this.#packungRoots()) {
      if (root.hidden) continue;
      const checked = root.querySelector('input[type="radio"]:checked');
      if (checked instanceof HTMLInputElement) return checked;
    }
    return null;
  }

  getPackCount() {
    const control = this.#packControl();
    if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return 1;
    const value = parseInt(control.value, 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  getVeSize() {
    const radio = this.#selectedPackung();
    if (!radio) return 1;
    const size = Number(radio.dataset.veSize);
    return Number.isFinite(size) && size > 0 ? size : 1;
  }

  getDiscountPct() {
    const radio = this.#selectedPackung();
    const pct = Number(radio?.dataset.discountPct);
    return Number.isFinite(pct) && pct > 0 ? pct : 0;
  }

  getPackungLabel() {
    return this.#selectedPackung()?.dataset.packungLabel || "";
  }

  getCartQuantity() {
    return this.getPackCount() * this.getVeSize();
  }

  sync() {
    const cartInput = this.#cartQuantityInput();
    if (!cartInput) return;

    const veSize = this.getVeSize();
    const packCount = this.getPackCount();
    const discountPct = this.getDiscountPct();
    const label = this.getPackungLabel();
    cartInput.value = String(this.getCartQuantity());

    const veSizeProp = this.root.querySelector('input[name="properties[_ve_size]"]');
    const packCountProp = this.root.querySelector('input[name="properties[_pack_count]"]');
    const labelProp = this.root.querySelector('input[name="properties[_packung]"]');
    const discountProp = this.root.querySelector('input[name="properties[_packung_discount]"]');
    if (veSizeProp instanceof HTMLInputElement) veSizeProp.value = String(veSize);
    if (packCountProp instanceof HTMLInputElement) packCountProp.value = String(packCount);
    if (labelProp instanceof HTMLInputElement) labelProp.value = label;
    if (discountProp instanceof HTMLInputElement) {
      discountProp.value = discountPct > 0 ? String(discountPct) : "";
      discountProp.disabled = discountPct <= 0;
    }

    this.root.dataset.veSize = String(veSize);
    this.root.dataset.vePackCount = String(packCount);
    this.root.dataset.veDiscountPct = String(discountPct);

    this.root.dispatchEvent(
      new CustomEvent("ve-quantity:sync", {
        bubbles: true,
        detail: {
          veSize,
          packCount,
          discountPct,
          cartQuantity: this.getCartQuantity(),
          packungLabel: label,
        },
      })
    );
  }
}

/** @type {WeakMap<HTMLElement, WeSiteVeQuantity>} */
const instances = new WeakMap();

function enhance(formComponent) {
  if (!(formComponent instanceof HTMLElement)) return;
  if (formComponent.dataset.veEnabled !== "true") return;
  if (instances.has(formComponent)) {
    instances.get(formComponent)?.sync();
    return;
  }
  instances.set(formComponent, new WeSiteVeQuantity(formComponent));
}

function scan(root = document) {
  root.querySelectorAll("product-form-component[data-ve-enabled='true']").forEach((el) => {
    enhance(/** @type {HTMLElement} */ (el));
  });
}

scan();

document.addEventListener("shopify:section:load", (event) => {
  const target = /** @type {CustomEvent} */ (event).target;
  if (target instanceof HTMLElement) scan(target);
});

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    mutation.addedNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (node.matches?.("product-form-component[data-ve-enabled='true']")) enhance(node);
      else if (node.querySelector) scan(node);
    });
  }
});
observer.observe(document.documentElement, { childList: true, subtree: true });

export { WeSiteVeQuantity };
